import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Icon from "components/ShellV2/Icon";
import { businessValidityBoundaryDate } from "utils/businessValidity";
import { formatDate } from "utils/time";

const PRESETS = [
  { key: "24h", translationKey: "time_horizon.last_24h", days: 1 },
  { key: "7d", translationKey: "time_horizon.last_7d", days: 7 },
  { key: "14d", translationKey: "time_horizon.last_14d", days: 14 },
  { key: "30d", translationKey: "time_horizon.last_30d", days: 30 },
  { key: "90d", translationKey: "time_horizon.last_90d", days: 90 },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function isoDate(d) {
  return `${String(d.getFullYear()).padStart(4, "0")}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate()
  )}`;
}

function localDate(year, month, day) {
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(year, month, day);
  return date;
}

function parseISO(s) {
  if (!s) return null;
  const parts = s.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return localDate(parts[0], parts[1] - 1, parts[2]);
}

function appLocale(language) {
  const baseLanguage = (language || "en").split("-")[0].toLowerCase();
  return { en: "en-US", es: "es-ES", pt: "pt-PT" }[baseLanguage] || language || "en-US";
}

function shifted(daysBack) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysBack + 1);
  return d;
}

function today() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function t0(d) {
  return d ? d.getTime() : 0;
}

function detectActivePreset(start, end) {
  const startD = parseISO(start);
  const endD = parseISO(end);
  if (!startD || !endD) return "custom";
  const todayD = today();
  if (t0(endD) !== t0(todayD)) return "custom";
  const found = PRESETS.find((p) => t0(startD) === t0(shifted(p.days)));
  return found ? found.key : "custom";
}

function Calendar({ from, to, onPick, allowFuture, maxDate }) {
  const { t, i18n } = useTranslation("common");
  const initial = from ? parseISO(from) : today();
  const [view, setView] = useState(() => localDate(initial.getFullYear(), initial.getMonth(), 1));
  const y = view.getFullYear();
  const m = view.getMonth();
  const startDow = (localDate(y, m, 1).getDay() + 6) % 7;
  const ndays = localDate(y, m + 1, 0).getDate();
  const fromD = parseISO(from);
  const toD = parseISO(to);
  const todayD = today();
  const maximumDate = parseISO(maxDate);
  const locale = appLocale(i18n.resolvedLanguage || i18n.language);
  const monthNames = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
    return Array.from({ length: 12 }, (_, index) => {
      const name = formatter.format(new Date(2020, index, 1));
      return name.charAt(0).toUpperCase() + name.slice(1);
    });
  }, [locale]);
  const weekdayNames = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
    // Some locales (e.g. pt-PT) return full names for "short"; cap to 3 chars
    // so the 7-column header never overflows.
    return Array.from({ length: 7 }, (_, index) =>
      formatter
        .format(new Date(2020, 5, index + 1))
        .replace(".", "")
        .slice(0, 3)
    );
  }, [locale]);

  const cells = useMemo(() => {
    const list = [];
    for (let i = 0; i < startDow; i += 1) list.push(null);
    for (let d = 1; d <= ndays; d += 1) list.push(localDate(y, m, d));
    return list;
  }, [y, m, startDow, ndays]);

  const goPrev = () => setView(localDate(y, m - 1, 1));
  const goNext = () => setView(localDate(y, m + 1, 1));
  const disablePrevious = allowFuture && y === 1 && m === 0;
  const disableNext = allowFuture
    ? y === 9999 && m === 11
    : y > todayD.getFullYear() || (y === todayD.getFullYear() && m >= todayD.getMonth());

  // Year dropdown so users can jump to past years directly (e.g. 2024) instead
  // of clicking the prev-month arrow many times.
  const yearOptions = [];
  for (let yr = todayD.getFullYear(); yr >= 2018; yr -= 1) yearOptions.push(yr);
  const selCls =
    "bg-transparent border-0 p-0 text-ink font-bold text-[13.5px] cursor-pointer outline-none";

  return (
    <div className="rec-cal">
      <div className="rec-cal-head">
        <button
          type="button"
          className="rec-cal-icon-btn"
          onClick={goPrev}
          disabled={disablePrevious}
          title={t("datePicker.previousMonth")}
        >
          <Icon name="chevron_left" />
        </button>
        <div className="flex items-center gap-[6px]">
          <select
            className={selCls}
            value={m}
            onChange={(e) => setView(localDate(y, Number(e.target.value), 1))}
            aria-label={t("datePicker.month")}
          >
            {monthNames.map((name, i) => (
              <option key={name} value={i}>
                {name}
              </option>
            ))}
          </select>
          {allowFuture ? (
            <input
              className={`${selCls} w-[58px]`}
              type="number"
              min="1"
              max="9999"
              value={y}
              onChange={(e) => {
                const year = Number(e.target.value);
                if (year >= 1 && year <= 9999) setView(localDate(year, m, 1));
              }}
              aria-label={t("datePicker.year")}
            />
          ) : (
            <select
              className={selCls}
              value={y}
              onChange={(e) => setView(localDate(Number(e.target.value), m, 1))}
              aria-label={t("datePicker.year")}
            >
              {yearOptions.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          )}
        </div>
        <button
          type="button"
          className="rec-cal-icon-btn"
          onClick={goNext}
          disabled={disableNext}
          title={t("datePicker.nextMonth")}
        >
          <Icon name="chevron_right" />
        </button>
      </div>
      <div className="rec-cal-grid">
        {weekdayNames.map((w) => (
          <span key={w} className="rec-cal-dow">
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          // eslint-disable-next-line react/no-array-index-key
          if (!d) return <span key={`empty-${y}-${m}-${i}`} />;
          const isStart = fromD && t0(d) === t0(fromD);
          const isEnd = toD && t0(d) === t0(toD);
          const inRange = fromD && toD && d >= fromD && d <= toD;
          const sel = isStart || isEnd;
          const classes = ["rec-cal-day"];
          if (inRange && !isStart && !isEnd) classes.push("rec-cal-day-inr");
          if (inRange && isStart) classes.push("rec-cal-day-inr", "start");
          if (inRange && isEnd) classes.push("rec-cal-day-inr", "end");
          if (sel) classes.push("rec-cal-day-sel");
          return (
            <button
              type="button"
              key={d.toISOString()}
              className={classes.join(" ")}
              disabled={(!allowFuture && d > todayD) || (maximumDate && d > maximumDate)}
              onClick={() => onPick(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

Calendar.propTypes = {
  from: PropTypes.string,
  to: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  allowFuture: PropTypes.bool,
  maxDate: PropTypes.string,
};

Calendar.defaultProps = {
  from: null,
  to: null,
  allowFuture: false,
  maxDate: undefined,
};

export default function DateRangePopover({
  value,
  onChange,
  mode,
  ariaLabel,
  disabled,
  allowFuture,
  portal,
}) {
  const { t } = useTranslation("common");
  const singleDate = mode === "single";
  const validityRange = mode === "validity";
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(() => detectActivePreset(value.start, value.end));
  const [from, setFrom] = useState(value.start || "");
  const [to, setTo] = useState(value.end || "");
  const [portalStyle, setPortalStyle] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      const insideTrigger = wrapRef.current?.contains(e.target);
      const insidePopover = popoverRef.current?.contains(e.target);
      if (!insideTrigger && !insidePopover) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !portal) return undefined;

    const updatePosition = () => {
      if (!triggerRef.current || !popoverRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const gap = 6;
      const viewportPadding = 8;
      const roomBelow = window.innerHeight - triggerRect.bottom - gap;
      const openAbove = roomBelow < popoverRect.height && triggerRect.top > roomBelow;
      const preferredTop = openAbove
        ? triggerRect.top - popoverRect.height - gap
        : triggerRect.bottom + gap;
      const maxTop = Math.max(
        viewportPadding,
        window.innerHeight - popoverRect.height - viewportPadding
      );
      const maxLeft = Math.max(
        viewportPadding,
        window.innerWidth - popoverRect.width - viewportPadding
      );

      setPortalStyle({
        position: "fixed",
        top: Math.max(viewportPadding, Math.min(preferredTop, maxTop)),
        left: Math.max(viewportPadding, Math.min(triggerRect.right - popoverRect.width, maxLeft)),
        right: "auto",
        zIndex: 120,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [from, mode, open, portal, preset, to]);

  useEffect(() => {
    setFrom(value.start || "");
    setTo(value.end || "");
    setPreset(detectActivePreset(value.start, value.end));
  }, [value.start, value.end]);

  const applyPreset = (p) => {
    const start = isoDate(shifted(p.days));
    const end = isoDate(today());
    setFrom(start);
    setTo(end);
    setPreset(p.key);
    onChange({ start, end });
    setOpen(false);
  };

  const enterCustom = () => {
    setPreset("custom");
  };

  const applyCustom = () => {
    if (!from || (!validityRange && !to)) return;
    const latestDate = businessValidityBoundaryDate("end");
    onChange({
      start: validityRange && from > latestDate ? latestDate : from,
      end: validityRange && to > latestDate ? latestDate : to,
    });
    setOpen(false);
  };

  const onPick = (d) => {
    const ds = isoDate(d);
    if (singleDate) {
      setFrom(ds);
      setTo(ds);
      onChange({ start: ds, end: ds });
      setOpen(false);
      return;
    }
    if (!from || (from && to)) {
      setFrom(ds);
      setTo("");
    } else if (parseISO(ds) < parseISO(from)) {
      setFrom(ds);
    } else {
      setTo(ds);
    }
  };

  const triggerLabel = (() => {
    if (singleDate) {
      const selectedDate = parseISO(value.start);
      return selectedDate ? formatDate(selectedDate) : t("datePicker.date");
    }
    if (!validityRange && preset === "24h") return t("time_horizon.last_24h");
    const startD = parseISO(value.start);
    const endD = parseISO(value.end);
    if (!startD && !endD) return t("datePicker.period");
    if (startD && !endD) {
      return `${formatDate(startD)} — …`;
    }
    if (!startD && endD) {
      return `… — ${formatDate(endD)}`;
    }
    return `${formatDate(startD)} — ${formatDate(endD)}`;
  })();

  const rangeLabel = (() => {
    const startD = parseISO(from);
    const endD = parseISO(to);
    if (!startD && !endD) return "—";
    if (startD && !endD) {
      return `${formatDate(startD)} – …`;
    }
    if (!startD && endD) {
      return `… – ${formatDate(endD)}`;
    }
    return `${formatDate(startD)} – ${formatDate(endD)}`;
  })();

  const renderPopover = (content, className) => {
    const popover = (
      <div
        ref={popoverRef}
        className={className}
        style={
          portal
            ? portalStyle || { position: "fixed", visibility: "hidden", zIndex: 120 }
            : undefined
        }
      >
        {content}
      </div>
    );
    return portal && typeof document !== "undefined"
      ? createPortal(popover, document.body)
      : popover;
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="rec-btn rec-btn-ghost"
        style={{ height: 36 }}
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        <Icon name="calendar_month" />
        {triggerLabel}
        <Icon name="expand_more" />
      </button>
      {open &&
        singleDate &&
        renderPopover(
          <div className="rec-dr-custom">
            <Calendar from={from} to={from} onPick={onPick} allowFuture={allowFuture} />
          </div>,
          "rec-pop rec-pop-wide"
        )}
      {open &&
        validityRange &&
        renderPopover(
          <div className="rec-dr-custom">
            <Calendar
              from={from}
              to={to}
              onPick={onPick}
              allowFuture
              maxDate={businessValidityBoundaryDate("end")}
            />
            <div className="rec-dr-range-lbl">{rangeLabel}</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rec-btn rec-btn-ghost rec-btn-sm"
                onClick={() => setFrom(businessValidityBoundaryDate("start"))}
              >
                <Icon name="first_page" />
                {t("businessValidity.earliestDate")}
              </button>
              <button
                type="button"
                className="rec-btn rec-btn-ghost rec-btn-sm"
                onClick={() => setTo(businessValidityBoundaryDate("end"))}
              >
                <Icon name="last_page" />
                {t("businessValidity.latestDate")}
              </button>
              <button
                type="button"
                className="rec-btn rec-btn-ghost rec-btn-sm"
                onClick={() => setTo("")}
              >
                <Icon name="all_inclusive" />
                {t("businessValidity.noEndDate")}
              </button>
            </div>
            <button
              type="button"
              className="rec-btn rec-btn-primary rec-btn-sm w-full"
              disabled={
                !from ||
                from > businessValidityBoundaryDate("end") ||
                (to && parseISO(to) < parseISO(from))
              }
              onClick={applyCustom}
            >
              <Icon name="check" />
              {t("datePicker.applyPeriod")}
            </button>
          </div>,
          "rec-pop rec-pop-wide"
        )}
      {open &&
        !singleDate &&
        !validityRange &&
        renderPopover(
          <>
            {PRESETS.map((p) => {
              const active = preset === p.key;
              return (
                <button
                  type="button"
                  key={p.key}
                  className={`rec-pop-opt${active ? " on" : ""}`}
                  onClick={() => applyPreset(p)}
                >
                  <span>{t(p.translationKey)}</span>
                  {active && <Icon name="check" className="ml-auto" style={{ fontSize: 16 }} />}
                </button>
              );
            })}
            <button
              type="button"
              className={`rec-pop-opt${preset === "custom" ? " on" : ""}`}
              onClick={enterCustom}
            >
              <span>{t("datePicker.custom")}</span>
              {preset === "custom" && (
                <Icon name="check" className="ml-auto" style={{ fontSize: 16 }} />
              )}
            </button>
            {preset === "custom" && (
              <div className="rec-dr-custom">
                <Calendar from={from} to={to} onPick={onPick} allowFuture={allowFuture} />
                <div className="rec-dr-range-lbl">{rangeLabel}</div>
                <button
                  type="button"
                  className="rec-btn rec-btn-primary rec-btn-sm w-full"
                  disabled={!from || !to}
                  onClick={applyCustom}
                >
                  <Icon name="check" />
                  {t("datePicker.applyPeriod")}
                </button>
              </div>
            )}
          </>,
          `rec-pop${preset === "custom" ? " rec-pop-wide" : ""}`
        )}
    </div>
  );
}

DateRangePopover.propTypes = {
  value: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["range", "single", "validity"]),
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  allowFuture: PropTypes.bool,
  portal: PropTypes.bool,
};

DateRangePopover.defaultProps = {
  mode: "range",
  ariaLabel: undefined,
  disabled: false,
  allowFuture: false,
  portal: false,
};
